/**
 * Microsoft Clarity tagging.
 *
 * Clarity is loaded and consent-gated by GTM, not by this cartridge, so window.clarity is frequently
 * still undefined when an exception fires. Every call goes through a queue that flushes once the
 * snippet installs itself; without it the calls are silently dropped and the numbers under-report.
 *
 * Clarity API reference: https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-api
 */

const QUEUE_TIMEOUT_MS = 10000;
const POLL_INTERVAL_MS = 250;

// Closed set. Keep it small and low-cardinality: the whole point is that these values are countable
// in the Clarity dashboard, which a per-session code can never be.
const EXCEPTION_TYPES = {
    CHECKOUT_TIMEOUT: 'checkout-timeout',
    CSRF_FAILURE: 'csrf-failure',
    PAYMENT_FAILURE: 'payment-failure',
    PAYMENT_VALIDATION: 'payment-validation',
    PLACEORDER_ERROR: 'placeorder-error',
    A2C_ERROR: 'a2c-error',
    JS_ERROR: 'js-error'
};

let pending = [];
let poller = null;

/**
 * Forwards everything buffered while window.clarity was undefined.
 *
 * @returns {boolean} true when the queue was flushed, false when Clarity is still absent
 */
function flush() {
    if (typeof window.clarity !== 'function') {
        return false;
    }

    const queued = pending;
    pending = [];
    queued.forEach(function (args) {
        window.clarity.apply(null, args);
    });

    return true;
}

/**
 * Calls Clarity, buffering until the GTM-delivered snippet has installed window.clarity.
 * Gives up after QUEUE_TIMEOUT_MS, which is the case where the shopper declined the consent
 * category that GTM gates Clarity behind.
 *
 * @param {...*} args - arguments forwarded verbatim to window.clarity
 * @returns {void}
 */
function call() {
    pending.push(Array.prototype.slice.call(arguments));

    if (flush() || poller) {
        return;
    }

    const deadline = Date.now() + QUEUE_TIMEOUT_MS;

    poller = setInterval(function () {
        if (flush() || Date.now() > deadline) {
            clearInterval(poller);
            poller = null;
            pending = [];
        }
    }, POLL_INTERVAL_MS);
}

/**
 * Tags the session with a critical event.
 *
 * Fires three things on purpose: the tag makes the session findable by filter, the event makes it
 * countable in the dashboard without having to pick a value first, and the upgrade keeps the session
 * from being sampled away (Clarity retains at most 100k recordings per project per day).
 *
 * @param {string} exceptionType - one of EXCEPTION_TYPES
 * @param {string} [sessionCode] - the XX-YY-ZZ code, when the response carried one
 * @returns {void}
 */
function trackException(exceptionType, sessionCode) {
    if (!exceptionType) {
        return;
    }

    call('set', 'exceptionType', exceptionType);
    call('event', 'exception:' + exceptionType);
    call('upgrade', exceptionType);

    if (sessionCode) {
        call('set', 'pageException', sessionCode);
    }
}

/**
 * Reads the Clarity identifiers out of the GTM customer data layer push. Those values come from the
 * uncached GTM-CustomerData remote include, so they are correct even on a cached page.
 *
 * @returns {Object|null} an object with clarityUserId and claritySessionId, or null when absent
 */
function getIdentityFromDataLayer() {
    const layer = window.dataLayer;

    if (!layer || !layer.length) {
        return null;
    }

    for (let i = layer.length - 1; i >= 0; i--) {
        if (layer[i] && layer[i].clarityUserId) {
            return layer[i];
        }
    }

    return null;
}

/**
 * Sends the custom identifiers to Clarity.
 *
 * Only the first argument is hashed by Clarity, which is why the session argument is the SHA-256
 * digest built server-side and never the raw SFCC session id.
 *
 * @returns {void}
 */
function identify() {
    const identity = getIdentityFromDataLayer();

    if (!identity) {
        return;
    }

    call('identify', identity.clarityUserId, identity.claritySessionId || '');
}

module.exports = {
    call: call,
    identify: identify,
    trackException: trackException,
    EXCEPTION_TYPES: EXCEPTION_TYPES
};

// Exposed for bundles that are not part of main.js (errorLogger.js is its own webpack entry and is
// deliberately dependency-free). Sharing the instance keeps one queue rather than two.
window.jwClarity = module.exports;
